import React, { useState } from 'react';

interface AddCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (companyData: any) => void;
  formLoading: boolean;
  formError: string | null;
  formSuccess: string | null;
  initialValues?: {
    companyName: string;
    address: string;
  };
}

const AddCompanyModal: React.FC<AddCompanyModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  formLoading,
  formError,
  formSuccess,
  initialValues
}) => {
  const [companyData, setCompanyData] = useState<{ companyName: string, address: string }>({
    companyName: '',
    address: ''
  });
  const [validationError, setValidationError] = useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      if (initialValues) {
        setCompanyData({
          companyName: initialValues.companyName || '',
          address: initialValues.address || ''
        });
      } else {
        setCompanyData({
          companyName: '',
          address: ''
        });
      }
    }
  }, [isOpen, initialValues]);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCompanyData(prev => ({
      ...prev,
      [name]: value
    }));
    setValidationError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!companyData.companyName.trim()) {
      setValidationError('Company Name is required');
      return;
    }
    if (!companyData.address.trim()) {
      setValidationError('Address is required');
      return;
    }

    onSubmit(companyData);
  };

  const handleClose = () => {
    setCompanyData({
      companyName: '',
      address: ''
    });
    onClose();
  };

  const isEditMode = !!initialValues;

  return (
    <div
      className="modal fade show"
      tabIndex={-1}
      style={{
        display: 'block',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1060,
      }}
      onClick={handleClose}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        style={{
          maxWidth: '600px',
          margin: '1.75rem auto', // Standard bootstrap margin
          minHeight: 'calc(100% - 3.5rem)' // Ensure full height scrollability
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content border-0 shadow-lg" style={{ maxHeight: '100%' }}>
          <div className="modal-header py-3" style={{
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            color: 'white',
            borderRadius: '0.3rem 0.3rem 0 0'
          }}>
            <h5 className="modal-title fw-bold">
              <i className="bi bi-building me-2"></i>
              {isEditMode ? 'Edit Company' : 'Add New Company'}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
              disabled={formLoading}
            ></button>
          </div>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="modal-body p-4" style={{ overflowY: 'auto' }}>
              {validationError && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {validationError}
                </div>
              )}
              {formError && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {formError}
                </div>
              )}
              {formSuccess && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  {formSuccess}
                </div>
              )}
              <div className="mb-3">
                <label className="form-label fw-medium text-start d-block">
                  <span className="text-danger me-1">*</span>Company Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="companyName"
                  value={companyData.companyName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter company name"
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-medium text-start d-block">
                  <span className="text-danger me-1">*</span>Address
                </label>
                <textarea
                  className="form-control"
                  name="address"
                  value={companyData.address}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter company address"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer py-3 bg-light">
              <button
                type="button"
                className="btn btn-outline-primary px-4 py-2"
                onClick={handleClose}
                disabled={formLoading}
                style={{
                  color: '#1e3c72',
                  borderColor: '#1e3c72',
                }}
                onMouseEnter={(e) => {
                  if (!formLoading) {
                    e.currentTarget.style.backgroundColor = '#1e3c72';
                    e.currentTarget.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#1e3c72';
                }}
              >
                <i className="bi bi-x-circle me-1"></i>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary px-4 py-2"
                disabled={formLoading}
                style={{
                  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                  border: 'none'
                }}
              >
                {formLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    {isEditMode ? 'Update Company' : 'Create Company'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCompanyModal;